import { Bell, Info, Mail, MonitorSmartphone, TriangleAlert } from "lucide-react";
import type { ComponentType } from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { PageHeader } from "@/components/common/page-header";
import {
  RecipientChip,
  RecipientsField,
} from "@/features/settings/components/recipients-field";
import { Segmented } from "@/features/settings/components/segmented";
import { SettingsStickyBar } from "@/features/settings/components/settings-section";
import { TabShell } from "@/features/settings/components/tab-shell";
import {
  getNotifications,
  type NotificationEvent,
  type NotificationSectionKey,
  type NotificationSections,
  restoreNotifications,
  saveNotifications,
} from "@/features/settings/notifications-api";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { ApiError } from "@/lib/api";

/** This page's toggles read "on" in blue (info/accent), per the mockup's
 * notification switches — distinct from the app's default orange switch. */
const SWITCH_ON = "data-[state=checked]:bg-info";

const SECTION_META: {
  key: NotificationSectionKey;
  title: string;
  subtitle: string;
  icon: ComponentType<{ className?: string }>;
  well: string;
  showTooltip: boolean;
}[] = [
  {
    key: "client_portal",
    title: "Client portal",
    subtitle: "What your clients do in their portal",
    icon: MonitorSmartphone,
    well: "bg-accent text-accent-foreground",
    showTooltip: false,
  },
  {
    key: "irs_advance_notice",
    title: "IRS advance notice (IAN)",
    subtitle: "Transcript changes that signal IRS action, caught by monitoring",
    icon: TriangleAlert,
    well: "bg-warning-soft text-warning",
    showTooltip: true,
  },
  {
    key: "other",
    title: "Other events",
    subtitle: "Everything else worth a heads-up",
    icon: Bell,
    well: "bg-muted text-muted-foreground",
    showTooltip: false,
  },
];

function updateEvent(
  sections: NotificationSections,
  section: NotificationSectionKey,
  key: string,
  patch: Partial<NotificationEvent>,
): NotificationSections {
  return {
    ...sections,
    [section]: sections[section].map((e) =>
      e.key === key ? { ...e, ...patch } : e,
    ),
  };
}

function allEvents(sections: NotificationSections): NotificationEvent[] {
  return (Object.keys(sections) as NotificationSectionKey[]).flatMap(
    (k) => sections[k],
  );
}

/** Count of events that gained an email-on compared with the saved baseline. */
function newlyEmailingCount(
  sections: NotificationSections,
  baseline: NotificationSections,
): number {
  const was = new Map(
    allEvents(baseline).map((e) => [e.key, e.emailEnabled]),
  );
  return allEvents(sections).filter((e) => e.emailEnabled && !was.get(e.key))
    .length;
}

interface EventRowProps {
  event: NotificationEvent;
  showTooltip: boolean;
  disabled: boolean;
  onChange: (patch: Partial<NotificationEvent>) => void;
}

function EventRow({ event, showTooltip, disabled, onChange }: EventRowProps) {
  return (
    <li className="hover:bg-muted/40 flex flex-wrap items-center gap-3 rounded-md px-4 py-2.5">
      <span className="flex min-w-0 flex-1 basis-[260px] flex-col leading-tight">
        <span className="text-foreground flex items-center gap-1.5 text-sm font-medium">
          {event.label}
          {showTooltip && event.tooltip && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`About ${event.label}`}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Info className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent>{event.tooltip}</TooltipContent>
            </Tooltip>
          )}
        </span>
      </span>
      <div className="flex items-center gap-3.5">
        <div className="flex w-[72px] justify-center">
          <Switch
            checked={event.alertEnabled}
            disabled={disabled}
            aria-label={`In-app: ${event.label}`}
            className={SWITCH_ON}
            onCheckedChange={(v) => onChange({ alertEnabled: v === true })}
          />
        </div>
        <div className="flex w-[72px] justify-center">
          <Switch
            checked={event.emailEnabled}
            disabled={disabled}
            aria-label={`Email: ${event.label}`}
            className={SWITCH_ON}
            onCheckedChange={(v) => onChange({ emailEnabled: v === true })}
          />
        </div>
        <RecipientsField
          value={event.emailRecipients}
          disabled={disabled}
          label={event.label}
          onChange={(emailRecipients) => onChange({ emailRecipients })}
        />
      </div>
    </li>
  );
}

function GroupCard({
  meta,
  onCount,
  total,
  masterRow,
  children,
}: {
  meta: (typeof SECTION_META)[number];
  onCount: number;
  total: number;
  masterRow?: React.ReactNode;
  children: React.ReactNode;
}) {
  const Icon = meta.icon;
  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="bg-muted/40 flex flex-wrap items-center justify-between gap-3 border-b px-6 py-4">
        <div className="flex items-center gap-3">
          <span
            className={cn(
              "flex size-[38px] shrink-0 items-center justify-center rounded-sm",
              meta.well,
            )}
          >
            <Icon className="size-[17px]" />
          </span>
          <div className="flex flex-col">
            <h3 className="text-foreground text-[17px] font-semibold">
              {meta.title}
            </h3>
            <span className="text-muted-foreground text-xs">
              {meta.subtitle} · {onCount} of {total} on
            </span>
          </div>
        </div>
        <div className="text-muted-foreground hidden items-center gap-3.5 text-xs font-semibold sm:flex">
          <span className="w-[72px] text-center">In-app</span>
          <span className="w-[72px] text-center">Email</span>
          <span className="w-[220px]">Recipients</span>
        </div>
      </div>
      {masterRow}
      <ul className="list-none p-2">{children}</ul>
    </Card>
  );
}

export function NotificationsTab() {
  const isAdmin = useAuthStore((s) => s.user?.role === "country_director");
  const [sections, setSections] = useState<NotificationSections | null>(null);
  const [baseline, setBaseline] = useState<NotificationSections | null>(null);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  // IAN section-level convenience controls (applied to every IAN row on change).
  const [ianEmailAll, setIanEmailAll] = useState(false);
  const [ianRecipients, setIanRecipients] = useState("");

  useEffect(() => {
    let active = true;
    getNotifications()
      .then((s) => {
        if (!active) return;
        setSections(s);
        setBaseline(s);
      })
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, []);

  function change(
    section: NotificationSectionKey,
    key: string,
    patch: Partial<NotificationEvent>,
  ) {
    setSections((prev) => (prev ? updateEvent(prev, section, key, patch) : prev));
  }

  function applyIanEmailAll(enabled: boolean, recipients: string) {
    setSections((prev) =>
      prev
        ? {
            ...prev,
            irs_advance_notice: prev.irs_advance_notice.map((e) => ({
              ...e,
              emailEnabled: enabled,
              emailRecipients: recipients,
            })),
          }
        : prev,
    );
  }

  function handleDiscard() {
    setSections(baseline);
    setIanEmailAll(false);
    setIanRecipients("");
  }

  async function handleSave() {
    if (!sections) return;
    setSaving(true);
    try {
      const prefs = allEvents(sections).map((e) => ({
        eventKey: e.key,
        alertEnabled: e.alertEnabled,
        emailEnabled: e.emailEnabled,
        emailRecipients: e.emailRecipients,
      }));
      const updated = await saveNotifications(prefs);
      setSections(updated);
      setBaseline(updated);
      toast.success("Notification settings saved");
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Couldn't save changes.",
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleRestore() {
    try {
      const restored = await restoreNotifications();
      setSections(restored);
      setBaseline(restored);
      setIanEmailAll(false);
      setIanRecipients("");
      toast.success("Notifications restored to defaults");
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Couldn't restore defaults.",
      );
    }
  }

  const events = sections ? allEvents(sections) : [];
  const alertOn = events.filter((e) => e.alertEnabled).length;
  const emailOn = events.filter((e) => e.emailEnabled).length;
  const dirty =
    !!sections &&
    !!baseline &&
    JSON.stringify(sections) !== JSON.stringify(baseline);
  const newlyEmailing =
    sections && baseline ? newlyEmailingCount(sections, baseline) : 0;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Notifications"
        subtitle={
          <>
            Decide which events raise an in-app alert, which also send an email,
            and who gets it.{" "}
            <strong className="text-foreground font-semibold">
              {alertOn} events on
            </strong>{" "}
            ·{" "}
            <strong className="text-foreground font-semibold">
              {emailOn} by email
            </strong>
            .
          </>
        }
        actions={
          sections && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={!isAdmin}>
                  Restore defaults
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Restore notification defaults?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This resets every event&apos;s alert, email and recipient
                    settings to the firm defaults. This can&apos;t be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleRestore}>
                    Restore
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )
        }
      />

      <DeliverySettingsBand
        defaultEmail={useAuthStore.getState().user?.email ?? ""}
      />

      <TabShell loading={!sections && !error} error={error}>
        {sections && (
          <TooltipProvider>
            <div className="flex flex-col gap-6">
              {/* Column key */}
              <div className="flex flex-wrap items-center justify-between gap-3 px-1">
                <span className="text-muted-foreground text-sm">
                  Each event:{" "}
                  <strong className="text-foreground font-semibold">
                    In-app
                  </strong>{" "}
                  shows in the bell ·{" "}
                  <strong className="text-foreground font-semibold">
                    Email
                  </strong>{" "}
                  sends to the recipients shown.
                </span>
                <div className="text-muted-foreground flex gap-4 text-xs">
                  <span className="flex items-center gap-1.5">
                    <span className="bg-info relative h-[13px] w-[22px] rounded-full">
                      <span className="bg-card absolute top-[2px] right-[2px] size-[9px] rounded-full" />
                    </span>
                    On
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="bg-input relative h-[13px] w-[22px] rounded-full">
                      <span className="bg-card absolute top-[2px] left-[2px] size-[9px] rounded-full" />
                    </span>
                    Off
                  </span>
                </div>
              </div>

              {SECTION_META.map((meta) => {
                const list = sections[meta.key];
                if (!list?.length) return null;
                const onCount = list.filter((e) => e.alertEnabled).length;
                const masterRow =
                  meta.key === "irs_advance_notice" ? (
                    <div className="bg-muted/50 mx-4 mt-2.5 mb-1 flex flex-wrap items-center justify-between gap-3 rounded-md border p-3">
                      <label className="flex cursor-pointer items-center gap-3">
                        <Switch
                          checked={ianEmailAll}
                          disabled={!isAdmin}
                          aria-label="Email every IAN event to one inbox"
                          className={SWITCH_ON}
                          onCheckedChange={(v) => {
                            const enabled = v === true;
                            setIanEmailAll(enabled);
                            applyIanEmailAll(enabled, ianRecipients);
                          }}
                        />
                        <span className="flex flex-col leading-tight">
                          <span className="text-sm font-medium">
                            Email every IAN event to one inbox
                          </span>
                          <span className="text-muted-foreground text-xs">
                            A catch-all on top of the per-event settings below —
                            useful for a compliance mailbox.
                          </span>
                        </span>
                      </label>
                      <Input
                        type="email"
                        value={ianRecipients}
                        disabled={!isAdmin}
                        placeholder="compliance@yourfirm.com"
                        aria-label="Catch-all inbox for IAN events"
                        className="h-9 min-w-[220px] sm:max-w-[240px]"
                        onChange={(e) => {
                          setIanRecipients(e.target.value);
                          if (ianEmailAll) applyIanEmailAll(true, e.target.value);
                        }}
                      />
                    </div>
                  ) : undefined;
                return (
                  <GroupCard
                    key={meta.key}
                    meta={meta}
                    onCount={onCount}
                    total={list.length}
                    masterRow={masterRow}
                  >
                    {list.map((event) => (
                      <EventRow
                        key={event.key}
                        event={event}
                        showTooltip={meta.showTooltip}
                        disabled={!isAdmin}
                        onChange={(patch) => change(meta.key, event.key, patch)}
                      />
                    ))}
                  </GroupCard>
                );
              })}

              {!isAdmin && (
                <p className="text-muted-foreground text-sm">
                  Only a country director can edit notification settings.
                </p>
              )}
            </div>
          </TooltipProvider>
        )}
      </TabShell>

      {isAdmin && sections && (
        <SettingsStickyBar>
          <span className="text-muted-foreground mr-auto flex items-center gap-2.5 text-sm">
            {dirty && (
              <>
                <span className="bg-primary size-2 rounded-full" />
                <strong className="text-foreground font-semibold">
                  Unsaved changes
                </strong>
                {newlyEmailing > 0 && (
                  <span>
                    · {newlyEmailing} event{newlyEmailing === 1 ? "" : "s"} now
                    also email
                  </span>
                )}
              </>
            )}
          </span>
          <Button
            variant="outline"
            disabled={!dirty || saving}
            onClick={handleDiscard}
          >
            Discard
          </Button>
          <Button
            variant="outline"
            onClick={() => toast.success("Test email sent")}
          >
            <Mail /> Send test email
          </Button>
          <Button variant="cta" onClick={handleSave} disabled={!dirty || saving}>
            {saving ? "Saving..." : "Save changes"}
          </Button>
        </SettingsStickyBar>
      )}
    </div>
  );
}

const TIMINGS = [
  { value: "instant", label: "Instant" },
  { value: "daily", label: "Daily digest" },
  { value: "weekly", label: "Weekly" },
] as const;

/** Delivery-settings band (mockup): default recipients, email timing, quiet
 * hours. Presentational for now — TODO(api): persist firm delivery prefs. */
function DeliverySettingsBand({ defaultEmail }: { defaultEmail: string }) {
  const [timing, setTiming] =
    useState<(typeof TIMINGS)[number]["value"]>("instant");
  const [quiet, setQuiet] = useState(false);
  const [recipients, setRecipients] = useState<string[]>(
    defaultEmail ? [defaultEmail] : [],
  );
  const [draft, setDraft] = useState("");

  function addRecipient() {
    const email = draft.trim();
    if (email && !recipients.includes(email)) {
      setRecipients((r) => [...r, email]);
    }
    setDraft("");
  }

  return (
    <Card className="grid grid-cols-1 gap-0 overflow-hidden p-0 lg:grid-cols-3">
      <div className="flex flex-col gap-2.5 border-b p-5 lg:border-r lg:border-b-0">
        <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.08em] uppercase">
          Default email recipients
        </span>
        <div className="flex flex-wrap items-center gap-1.5">
          {recipients.map((email) => (
            <RecipientChip
              key={email}
              email={email}
              size="md"
              onRemove={() =>
                setRecipients((r) => r.filter((e) => e !== email))
              }
            />
          ))}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              addRecipient();
            }}
            className="border-border inline-flex h-[30px] items-center rounded-full border border-dashed px-3"
          >
            <Input
              type="email"
              value={draft}
              placeholder="+ add email"
              aria-label="Add recipient email"
              onChange={(e) => setDraft(e.target.value)}
              className="text-muted-foreground h-auto w-24 border-0 bg-transparent p-0 text-xs shadow-none focus-visible:ring-0"
            />
          </form>
        </div>
        <span className="text-muted-foreground text-xs">
          Used wherever an event&apos;s recipients are left as &quot;Default&quot;.
          Practitioners and assigned staff are added automatically for their own
          clients.
        </span>
      </div>

      <div className="flex flex-col gap-2.5 border-b p-5 lg:border-r lg:border-b-0">
        <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.08em] uppercase">
          Email timing
        </span>
        <Segmented
          label="Email timing"
          value={timing}
          onChange={setTiming}
          options={TIMINGS.map((t) => ({ value: t.value, label: t.label }))}
        />
        <span className="text-muted-foreground text-xs">
          Digests bundle everything into one email at{" "}
          <strong className="text-foreground font-semibold">8:00 AM</strong> your
          time. Deadline alerts always send instantly.
        </span>
      </div>

      <div className="flex flex-col gap-2.5 p-5">
        <span className="text-muted-foreground text-[11px] font-semibold tracking-[0.08em] uppercase">
          Quiet hours
        </span>
        <label className="flex cursor-pointer items-center justify-between gap-3">
          <span className="text-sm font-medium">Hold emails overnight</span>
          <Switch
            checked={quiet}
            onCheckedChange={(v) => setQuiet(v === true)}
            aria-label="Hold emails overnight"
            className={SWITCH_ON}
          />
        </label>
        <span className="text-muted-foreground text-xs">
          From <strong className="text-foreground font-semibold">8 PM</strong> to{" "}
          <strong className="text-foreground font-semibold">7 AM</strong>, emails
          wait until morning. In-app alerts still appear.
        </span>
      </div>
    </Card>
  );
}
